import { Box, CircularProgress, Paper } from "@mui/material"
import { useNavigate, useParams, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import React, { useEffect } from "react"
import { useQuery } from "urql"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { GetCompanyNameByIdDocument } from "@/graphql/codegen/graphql"
import { CustomerProductLocationSection } from "@/screens/CustomerProducts/components/CustomerProductLocationSection"
import { useProductPreferenceAssociationList } from "@/screens/CustomerProducts/hooks/useProductPreferenceAssociationList"
import ProductApprovalPerLocationPanel from "@/screens/Products/components/ProductApprovalPerLocationPanel"
import ProductPreferencesHeader from "@/screens/Products/components/ProductPreferencesHeader"
import "@/screens/Products/graphql/companyName.query"
import { selectProduct, useProductActions, useProductStore } from "@/stores/useProductStore"
import theme from "@/styles/mui-theme"

export const ProductPreferencesContainer: FC = () => {
  const { productId } = useParams({
    from: "/app/product/$productId/preference/",
  })

  const { companyId } = useSearch({
    from: "/app/product/$productId/preference/",
  })

  const navigate = useNavigate()

  useEffect(() => {
    if (!companyId) {
      navigate({ to: "/app/product/$productId/edit/", params: { productId } })
    }
  }, [companyId, navigate, productId])

  const { getProduct } = useProductActions()

  useEffect(() => {
    if (productId) {
      getProduct(Number(productId))
    }
  }, [productId, getProduct])

  const product = useProductStore(selectProduct)
  const fetchingProduct = useProductStore((state) => state?.fetching)

  const { associations, isFetching, tableContainerRef, handleScroll, refetch } = useProductPreferenceAssociationList({
    companyId,
    productId: Number(productId),
  })

  const companyProductAssociationId = associations[0]?.companyProductAssociationId ?? null
  const customerCompanyName = associations[0]?.company?.name ?? null
  const customerDescription = associations[0]?.customerDescription ?? ""
  const customerCodes = associations[0]?.customerCode ?? []

  const isAssociationRemoved = !!companyId && !isFetching && associations.length === 0

  const [{ data: companyData }] = useQuery({
    query: GetCompanyNameByIdDocument,
    variables: { companyId: companyId ?? "" },
    pause: !isAssociationRemoved,
  })

  if (!companyId) return null

  const removedAssociationCompanyName =
    companyData?.company?.get?.__typename === "CompanyGetSuccess" ? companyData.company.get.company.name : null

  if (!product || fetchingProduct) {
    return (
      <Box className='flex w-full items-center justify-center'>
        <CircularProgress
          size={48}
          className='transition-opacity duration-300 ease-in-out'
          sx={{
            color: theme.palette.primary.main,
          }}
        />
      </Box>
    )
  }

  const displayCompanyName = isAssociationRemoved ? removedAssociationCompanyName : customerCompanyName

  return (
    <Box className='mt-8 w-full px-6 pb-6'>
      <div className='flex h-full flex-col gap-4'>
        <Paper elevation={0} className='flex min-h-0 grow flex-col bg-white'>
          <Box className='p-6 pb-0'>
            <ProductPreferencesHeader
              productDetails={product}
              companyId={companyId}
              customerCompanyName={displayCompanyName}
              customerDescription={isAssociationRemoved ? undefined : customerDescription}
              customerCodes={isAssociationRemoved ? undefined : customerCodes}
            />
          </Box>
          <Box className='flex flex-col space-y-7 overflow-y-auto p-6 pt-7'>
            {isAssociationRemoved ? (
              <InformationMissingAlert
                title={`This product is no longer associated with ${displayCompanyName ?? "this company"}`}
              />
            ) : (
              <>
                <ProductApprovalPerLocationPanel
                  associations={associations}
                  companyProductAssociationId={companyProductAssociationId}
                  companyId={companyId}
                  productId={Number(productId)}
                  productPublicId={product.publicId ?? undefined}
                  tableContainerRef={tableContainerRef}
                  handleScroll={handleScroll}
                  refetch={refetch}
                />
                <CustomerProductLocationSection
                  associations={associations}
                  companyId={companyId}
                  productId={Number(productId)}
                />
              </>
            )}
          </Box>
        </Paper>
      </div>
    </Box>
  )
}
